import { useCallback, useEffect, useRef, useState } from "react";
import type { Appointment, Invoice } from "@dentacore/shared-types";
import {
  INVOICE_STATUS_LABELS,
  minorUnitExponent,
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHODS,
} from "@dentacore/shared-types";

import { getBilling, recordPayment } from "../api/patients";
import { useCan } from "../auth/use-can";
import { formatMoney, parseMoney } from "../patients/money";
import styles from "./appointment-payment.module.css";
import { RequiredMark } from "../ui/required-mark";
import { useBranchCurrency } from "../api/use-branch-currency";
import { useBookingScoped } from "./booking-location";
import { atLocation } from "../api/client";

/** The balance as the text a money box starts with: `1200` in a zero-decimal currency, `12.00` in a two-decimal one. */
function boxText(cents: number, of: string): string {
  const decimals = minorUnitExponent(of);
  return (cents / 10 ** decimals).toFixed(decimals);
}

/**
 * Recording the payment for a finished visit, from the visit itself
 * (STORY-187, PM-D1–PM-D4).
 *
 * Closing a visit already charts the work and raises its invoice; the money was
 * only ever recorded afterwards, from the patient's Billing tab. This puts the
 * same step on the **completed appointment**: its invoice, what is paid and
 * what is owed, and a *Record payment* button while anything is owed.
 *
 * **It adds no new money rule.** It reads the patient's billing as the Billing
 * tab does, picks the invoice(s) raised for *this* appointment, and records
 * through the same call — so the overpayment guard, the status re-derivation
 * and the accounting figures are the ones already there. A payment may not
 * exceed what the invoice owes; the amount starts at exactly that and may be
 * lowered for a part payment (PM-D2).
 *
 * **Only for someone who may record payments** (`billing.payment.record`); for
 * everyone else the section is not drawn at all, as the Billing tab's button is
 * not (the server refuses regardless).
 */
export function AppointmentPayment({
  appointment,
  onRecorded,
  openFirst,
}: {
  appointment: Appointment;
  /** A payment was recorded here — so a notice that one was not is no longer true. */
  onRecorded?: () => void;
  /**
   * The quick ring's *Take payment* (STORY-221): each new number presses
   * *Record payment* on the first invoice that still owes, once they have
   * loaded — the same form the button opens, nothing more.
   */
  openFirst?: number;
}): React.JSX.Element | null {
  const here = useBookingScoped();
  const can = useCan();
  const currency = useBranchCurrency();
  const allowed = can("billing.payment.record");
  const patientId = appointment.patient?.id ?? null;
  const done = appointment.status === "COMPLETED";

  /** This visit's invoices; null while they load. */
  const [invoices, setInvoices] = useState<readonly Invoice[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** The invoice whose payment form is open, if any. */
  const [paying, setPaying] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<string>("CASH");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (patientId === null) return;
      const billing = await here(getBilling)(patientId, signal);
      setInvoices(
        billing.invoices.filter(
          (invoice) =>
            // A draft is not yet a bill and a void has been withdrawn: neither takes money.
            invoice.status !== "DRAFT" &&
            invoice.status !== "VOID" &&
            // The invoice's appointment is that of its first line, and closing a visit raises
            // one invoice per appointment. An invoice made by hand from several visits' lines
            // shows under the first of them only — it is still all on the Billing tab.
            invoice.appointmentId === appointment.id,
        ),
      );
    },
    [patientId, appointment.id],
  );

  useEffect(() => {
    if (!allowed || !done || patientId === null) return;
    const controller = new AbortController();
    setInvoices(null);
    setLoadError(null);
    setPaying(null);
    load(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      setLoadError(cause instanceof Error ? cause.message : "The payment could not be read.");
    });
    return () => controller.abort();
  }, [allowed, done, patientId, load]);

  // The ring's request, honoured once per number and only once there is something to pay.
  const openedFor = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (openFirst === undefined || openedFor.current === openFirst || invoices === null) return;
    openedFor.current = openFirst;
    const owing = invoices.find((invoice) => invoice.outstandingCents > 0);
    if (owing === undefined) return;
    setError(null);
    setPaying(owing.id);
    setAmount(boxText(owing.outstandingCents, owing.currency));
    setMethod("CASH");
  }, [openFirst, invoices]);

  if (!allowed || !done || patientId === null) return null;

  // An invoice is in the currency it was written in (QA4-D6) — the location's
  // own, unless it changed currency since this visit was billed.
  const money = (cents: number, of: string = currency): string => formatMoney(cents, of);

  const open = (invoice: Invoice): void => {
    setError(null);
    setPaying(invoice.id);
    setAmount(boxText(invoice.outstandingCents, invoice.currency));
    setMethod("CASH");
  };

  const record = (invoice: Invoice): void => {
    const cents = parseMoney(amount, invoice.currency);
    if (cents === null) {
      setError("That amount is not a number above zero.");
      return;
    }
    if (cents > invoice.outstandingCents) {
      setError(`That is more than is owed — ${money(invoice.outstandingCents, invoice.currency)}.`);
      return;
    }
    setBusy(true);
    setError(null);
    here(recordPayment)(patientId, invoice.id, { amountCents: cents, method })
      .then(async () => {
        setPaying(null);
        onRecorded?.();
        await load();
      })
      .catch((cause: unknown) => {
        // The server's own sentence — e.g. somebody else paid it in the meantime.
        setError(cause instanceof Error ? cause.message : "That payment did not save.");
      })
      .finally(() => setBusy(false));
  };

  return (
    <div className={styles.payment} data-testid="panel-payment">
      <span className={styles.label}>Payment</span>

      {loadError !== null && <p className={styles.note}>{loadError}</p>}
      {loadError === null && invoices === null && <p className={styles.note}>Loading…</p>}
      {invoices !== null && invoices.length === 0 && (
        <p className={styles.note} data-testid="panel-payment-none">
          No invoice was raised for this visit — nothing was charted, so there is nothing to
          collect.
        </p>
      )}

      {invoices?.map((invoice) => (
        <div key={invoice.id} className={styles.invoice} data-testid="panel-payment-invoice">
          <div className={styles.line}>
            <span className={styles.number}>{invoice.number}</span>
            <span className={styles.status}>{INVOICE_STATUS_LABELS[invoice.status]}</span>
          </div>
          <div className={styles.figures}>
            <span>Total {money(invoice.totalCents, invoice.currency)}</span>
            <span>Paid {money(invoice.paidCents, invoice.currency)}</span>
            <span className={invoice.outstandingCents > 0 ? styles.owed : styles.settled}>
              {invoice.outstandingCents > 0
                ? `Owed ${money(invoice.outstandingCents, invoice.currency)}`
                : "Paid in full"}
            </span>
          </div>

          {invoice.outstandingCents > 0 && paying !== invoice.id && (
            <button
              type="button"
              className={styles.record}
              onClick={() => open(invoice)}
              data-testid="panel-payment-open"
            >
              Record payment
            </button>
          )}

          {paying === invoice.id && (
            <form
              className={styles.form}
              onSubmit={(event) => {
                event.preventDefault();
                record(invoice);
              }}
              data-testid="panel-payment-form"
            >
              <label className={styles.field}>
                <span className={styles.fieldLabel}>
                  Amount
                  <RequiredMark />
                </span>
                <input
                  className={styles.input}
                  value={amount}
                  onChange={(event) => {
                    setError(null);
                    setAmount(event.target.value);
                  }}
                  inputMode="decimal"
                  aria-label="Payment amount"
                  data-testid="panel-payment-amount"
                />
              </label>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>
                  Method
                  <RequiredMark />
                </span>
                <select
                  className={styles.input}
                  value={method}
                  onChange={(event) => setMethod(event.target.value)}
                  aria-label="Payment method"
                  data-testid="panel-payment-method"
                >
                  {PAYMENT_METHODS.map((name) => (
                    <option key={name} value={name}>
                      {PAYMENT_METHOD_LABELS[name]}
                    </option>
                  ))}
                </select>
              </label>
              <div className={styles.actions}>
                <button
                  type="submit"
                  className={styles.record}
                  disabled={busy || amount.trim() === ""}
                  data-testid="panel-payment-save"
                >
                  Record
                </button>
                <button
                  type="button"
                  className={styles.cancel}
                  disabled={busy}
                  onClick={() => {
                    setPaying(null);
                    setError(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      ))}

      {error !== null && (
        <p className={styles.problem} role="alert" data-testid="panel-payment-error">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * What was paid as the visit closed (FX30) — chosen in the Complete dialog,
 * before the invoice exists, so it is said as "all of it" or "this much"
 * rather than as a figure checked against a bill nobody has seen yet.
 */
export type VisitPayment =
  { kind: "full"; method: string } | { kind: "part"; amountCents: number; method: string };

/**
 * Record a payment chosen in the Complete dialog, once the visit has closed
 * and its invoice been raised (FX30, superseding PM-D1's button-only rule).
 *
 * **The same call the panel's button makes** (PM-D4), against the invoice
 * whose appointment is this one. Resolves to null when it landed, or to the
 * sentence to show when it did not — the visit is complete either way, and
 * the panel's *Record payment* is the way to try again.
 */
export async function payForVisit(
  patientId: string,
  appointmentId: string,
  payment: VisitPayment,
  money: (cents: number) => string,
  /** The visit's own location, on the combined calendar (CF-D15); null for the active one. */
  branchId: string | null = null,
): Promise<string | null> {
  try {
    const billing = await atLocation(branchId, () => getBilling(patientId));
    const invoice = billing.invoices.find(
      (row) =>
        row.appointmentId === appointmentId &&
        row.status !== "DRAFT" &&
        row.status !== "VOID" &&
        row.outstandingCents > 0,
    );
    if (invoice === undefined) {
      return "The visit is complete, but no invoice was raised for it, so the payment was not recorded.";
    }
    const amountCents = payment.kind === "full" ? invoice.outstandingCents : payment.amountCents;
    if (amountCents > invoice.outstandingCents) {
      return `The visit is complete, but ${money(amountCents)} is more than its invoice owes (${money(invoice.outstandingCents)}), so nothing was recorded. Record it from the panel.`;
    }
    await atLocation(branchId, () =>
      recordPayment(patientId, invoice.id, { amountCents, method: payment.method }),
    );
    return null;
  } catch (cause) {
    return `The visit is complete, but the payment did not save: ${
      cause instanceof Error ? cause.message : "unknown reason"
    }. Record it from the panel.`;
  }
}

/**
 * **One payment for a whole visit, split across its bills in order** (FV-D7):
 * the first patient's first, then the next, each recorded on that patient's own
 * invoice so every file stays right. *Paid in full* settles every bill; a part
 * payment fills them in turn and leaves the later ones owing. An ordinary visit
 * is a family of one, and is paid exactly as `payForVisit` pays it.
 */
export async function payForVisits(
  members: readonly Appointment[],
  payment: VisitPayment,
  money: (cents: number) => string,
  branchId: string | null = null,
): Promise<string | null> {
  const people = members.filter((member) => member.patient !== null);
  const [only] = people;
  if (people.length === 1 && only?.patient != null) {
    return payForVisit(only.patient.id, only.id, payment, money, branchId);
  }

  let left = payment.kind === "full" ? Number.POSITIVE_INFINITY : payment.amountCents;
  try {
    for (const member of people) {
      if (left <= 0 || member.patient === null) break;
      const billing = await atLocation(branchId, () => getBilling(member.patient?.id ?? ""));
      const invoice = billing.invoices.find(
        (row) =>
          row.appointmentId === member.id &&
          row.status !== "DRAFT" &&
          row.status !== "VOID" &&
          row.outstandingCents > 0,
      );
      if (invoice === undefined) continue;
      const amountCents = Math.min(left, invoice.outstandingCents);
      await atLocation(branchId, () =>
        recordPayment(member.patient?.id ?? "", invoice.id, {
          amountCents,
          method: payment.method,
        }),
      );
      left -= amountCents;
    }
  } catch (cause) {
    return `The visit is complete, but a payment did not save: ${
      cause instanceof Error ? cause.message : "unknown reason"
    }. Record it from each patient's file.`;
  }
  if (payment.kind === "part" && left > 0) {
    return `The visit is complete, but ${money(left)} of that is more than the bills owe, so it was not recorded.`;
  }
  return null;
}
